import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	Search,
	Filter,
	ChevronLeft,
	ChevronRight,
	Building2,
	Mail,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { getAccessToken } from "@/services/auth";
import { adminApiUrl } from "@/config";

// Fetch functions
async function fetchUsers(params: Record<string, any>) {
	const token = getAccessToken();
	const queryParams = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== "") {
			queryParams.append(key, String(value));
		}
	});
	const res = await fetch(adminApiUrl(`/users?${queryParams.toString()}`), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchUserStats() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/users/stats"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

const employmentTypes = ["CONTRACT", "PERMANENT", "INTERN", "PROBATION"];

export function UserList() {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [isActive, setIsActive] = useState<string>("");
	const [employmentType, setEmploymentType] = useState("");
	const [showFilters, setShowFilters] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["admin-users", page, search, isActive, employmentType],
		queryFn: () =>
			fetchUsers({
				page,
				limit: 20,
				search: search || undefined,
				isActive: isActive || undefined,
				employmentType: employmentType || undefined,
			}),
	});

	const { data: stats } = useQuery({
		queryKey: ["admin-user-stats"],
		queryFn: fetchUserStats,
	});

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setSearch(searchInput);
		setPage(1);
	};

	const clearFilters = () => {
		setSearch("");
		setSearchInput("");
		setIsActive("");
		setEmploymentType("");
		setPage(1);
	};

	const hasFilters = search || isActive || employmentType;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Users</h1>
					<p className="text-gray-500 mt-1">
						Manage all users across all organizations
					</p>
				</div>
			</div>

			{/* Stats */}
			{stats && (
				<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
					<Card>
						<CardContent className="p-4">
							<p className="text-2xl font-bold">{stats.total}</p>
							<p className="text-sm text-gray-500">Total Users</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<p className="text-2xl font-bold text-green-600">{stats.active}</p>
							<p className="text-sm text-gray-500">Active</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<p className="text-2xl font-bold text-gray-500">{stats.inactive}</p>
							<p className="text-sm text-gray-500">Inactive</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<p className="text-2xl font-bold text-red-500">{stats.deleted}</p>
							<p className="text-sm text-gray-500">Deleted</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<p className="text-2xl font-bold text-blue-600">
								{stats.recentlyAdded}
							</p>
							<p className="text-sm text-gray-500">Added (30d)</p>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Search and Filters */}
			<Card>
				<CardContent className="p-4">
					<div className="flex flex-col md:flex-row gap-4">
						<form onSubmit={handleSearch} className="flex-1 flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<Input
									placeholder="Search users by email or name..."
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									className="pl-10"
								/>
							</div>
							<Button type="submit" variant="outline">
								Search
							</Button>
						</form>
						<Button
							variant="outline"
							onClick={() => setShowFilters(!showFilters)}
							className={cn(showFilters && "bg-gray-100")}
						>
							<Filter className="w-4 h-4 mr-2" />
							Filters
							{hasFilters && (
								<span className="ml-2 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
									{[search, isActive, employmentType].filter(Boolean).length}
								</span>
							)}
						</Button>
					</div>

					{/* Filter Panel */}
					{showFilters && (
						<div className="mt-4 pt-4 border-t flex flex-wrap gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Status
								</label>
								<select
									value={isActive}
									onChange={(e) => {
										setIsActive(e.target.value);
										setPage(1);
									}}
									className="px-3 py-2 border rounded-md text-sm"
								>
									<option value="">All Statuses</option>
									<option value="true">Active</option>
									<option value="false">Inactive</option>
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Employment Type
								</label>
								<select
									value={employmentType}
									onChange={(e) => {
										setEmploymentType(e.target.value);
										setPage(1);
									}}
									className="px-3 py-2 border rounded-md text-sm"
								>
									<option value="">All Types</option>
									{employmentTypes.map((type) => (
										<option key={type} value={type}>
											{type}
										</option>
									))}
								</select>
							</div>
							{hasFilters && (
								<div className="flex items-end">
									<Button variant="ghost" size="sm" onClick={clearFilters}>
										Clear Filters
									</Button>
								</div>
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Users Table */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span>Users</span>
						{data && (
							<span className="text-sm font-normal text-gray-500">
								{data.pagination.total} total
							</span>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
						</div>
					) : data?.users?.length === 0 ? (
						<div className="text-center py-12 text-gray-500">No users found</div>
					) : (
						<>
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr className="border-b">
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												User
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Organization
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Status
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Type
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Created
											</th>
										</tr>
									</thead>
									<tbody>
										{data?.users?.map((user: any) => (
											<tr
												key={user._id}
												className="border-b last:border-0 hover:bg-gray-50"
											>
												<td className="py-3 px-4">
													<div className="flex items-center gap-3">
														<div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
															<Mail className="w-5 h-5 text-gray-400" />
														</div>
														<div>
															<p className="font-medium text-gray-900">
																{user.name ||
																	user.firstName ||
																	user.email.split("@")[0]}
															</p>
															<p className="text-sm text-gray-500">
																{user.email}
															</p>
														</div>
													</div>
												</td>
												<td className="py-3 px-4">
													{user.organization ? (
														<div className="flex items-center gap-2">
															<Building2 className="w-4 h-4 text-gray-400" />
															<span className="text-sm">
																{user.organization.name}
															</span>
															<Badge className="bg-gray-100 text-gray-700 text-xs">
																{user.organization.plan}
															</Badge>
														</div>
													) : (
														<span className="text-sm text-gray-400">-</span>
													)}
												</td>
												<td className="py-3 px-4">
													<Badge
														className={cn(
															user.isActive
																? "bg-green-100 text-green-700"
																: "bg-gray-100 text-gray-700",
														)}
													>
														{user.isActive ? "Active" : "Inactive"}
													</Badge>
												</td>
												<td className="py-3 px-4 text-sm text-gray-600">
													{user.employmentType || "-"}
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{user.createdAt
														? new Date(user.createdAt).toLocaleDateString()
														: "-"}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							{data && data.pagination.pages > 1 && (
								<div className="flex items-center justify-between mt-4 pt-4 border-t">
									<p className="text-sm text-gray-500">
										Page {data.pagination.page} of {data.pagination.pages}
									</p>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											disabled={page <= 1}
											onClick={() => setPage((p) => p - 1)}
										>
											<ChevronLeft className="w-4 h-4 mr-1" />
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={page >= data.pagination.pages}
											onClick={() => setPage((p) => p + 1)}
										>
											Next
											<ChevronRight className="w-4 h-4 ml-1" />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
